// utils/htmlToPdfElements.js
import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { parseDocument } from 'htmlparser2';

const styles = StyleSheet.create({
  bold: { fontWeight: 'bold' },
  italic: { fontStyle: 'italic' },
  underline: { textDecoration: 'underline' },
  strikethrough: { textDecoration: 'line-through' },
  paragraph: { fontSize: 10, marginBottom: 3, lineHeight: 1.3 },
  bullet: { flexDirection: 'row', marginBottom: 2 },
  bulletSymbol: { marginRight: 4 },
  normal: { fontSize: 10 },
});

const isInlineTag = (tag) => ['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span'].includes(tag);

function parseInlineChildren(children, keyPrefix = '') {
  return children.map((child, index) => {
    const key = `${keyPrefix}-${index}`;

    if (child.type === 'text') {
      return <Text key={key}>{child.data}</Text>;
    }

    if (child.type === 'tag') {
      const inner = parseInlineChildren(child.children || [], key);
      switch (child.name) {
        case 'strong':
        case 'b':
          return (
            <Text key={key} style={{fontFamily: 'NOTO-BOLD'}}>
              {inner}
            </Text>
          );
        case 'em':
        case 'i':
          return (
            <Text key={key} style={{ fontFamily: 'NOTO-ITALIC' }}>
              {inner}
            </Text>
          );
        case 'u':
          return (
            <Text key={key} style={styles.underline}>
              {inner}
            </Text>
          );
        case 's':
        case 'strike':
        case 'del':
          return (
            <Text key={key} style={styles.strikethrough}>
              {inner}
            </Text>
          );
        case 'span':
          return <Text key={key}>{inner}</Text>;
        default:
          return <Text key={key}>{inner}</Text>;
      }
    }

    return null;
  });
}

export const convertHtmlToPdf = (html) => {
  const dom = parseDocument(html);
  const result = [];

  dom.children.forEach((node, index) => {
    const key = `node-${index}`;

    if (node.type === 'text') {
      result.push(
        <Text key={key} style={styles.paragraph}>
          {node.data}
        </Text>
      );
    }

    if (node.type === 'tag') {
      const tag = node.name;

      // Block-level elements
      if (tag === 'p') {
        result.push(
          <Text key={key} style={styles.paragraph}>
            {parseInlineChildren(node.children || [], key)}
          </Text>
        );
      }

      if (tag === 'br') {
        result.push(
          <Text key={key} style={styles.paragraph}>
            {"\n"}
          </Text>
        );
      }

      // Bullets (ul / li)
      if (tag === 'ul' || tag === 'ol') {
        const isOrdered = tag === 'ol';
        let count = 1;
        (node.children || []).forEach((liNode, liIndex) => {
          if (liNode.name === 'li') {
            result.push(
              <View key={`${key}-li-${liIndex}`} style={styles.bullet}>
                <Text style={styles.bulletSymbol}>
                  {isOrdered ? `${count++}.` : '•'}
                </Text>
                <Text style={styles.normal}>
                  {parseInlineChildren(liNode.children || [], `${key}-li-${liIndex}`)}
                </Text>
              </View>
            );
          }
        });
      }
    }
  });

  return result;
};